import ReferenceIcon from '@assets/icons/note.svg?react'
import RecipientIcon from '@assets/icons/user.svg?react'
import {
  CoreApplicationFeeAnnounce,
  CoreInput,
  CoreOnboardingAnnounce,
  CorePaymentInput,
  CoreRadioButton,
  CoreSelect,
  MMButton,
  RecurringToggleBox,
  SendMoneyMethodInfo,
  SuccessPane
} from '@components'
import { useHeaderContext } from '@context/HeaderContext'
import { toastSuccess } from '@utils'
import BankRecipientModal from '@views/recipients/BankRecipientModal/BankRecipientModal'
import AccountingCategorySelect from '@withdrawals/components/AccountingCategorySelect'
import FileUploadPane from '@withdrawals/components/FileUploadPane'
import NoReferenceModal from '@withdrawals/components/NoReferenceModal'
import NoSuitableAccountModal from '@withdrawals/components/NoSuitableAccountModal'
import ProviderSpecificFields from '@withdrawals/components/ProviderSpecificFields'
import UploadedFilesPane from '@withdrawals/components/UploadedFilesPane'
import styles from '@withdrawals/styles/WithdrawInitiate.module.scss'
import BankTransferPreview from '@withdrawals/views/BankTransferPreview'
import {
  extractOptionStatusFromAccount,
  MerchantAccountTypes,
  PaymentOptionStatuses,
  paths,
  responseDoesNotContainsChallenge,
  selectMerchantAccountType,
  useAppSelector,
  useBankTransferQry,
  useProviderSpecificFieldsQry,
  useReturnToOrigin,
  useShouldPayApplicationFeeQry,
  useTransactionFilesManagementQry
} from 'mmfintech-backend-api'
import type { WithdrawalSubFlow } from 'mmfintech-backend-api/types'
import {
  formatIban,
  GlobalContext,
  hasProperty,
  isValidArray,
  isValidObject,
  isValidString,
  OtpContext,
  tr
} from 'mmfintech-commons'
import { ErrorDisplay, Spinner } from 'mmfintech-portal-commons'
import { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

const BankTransferSteps = {
  INITIAL: 'INITIAL',
  PROVIDER_SPECIFIC_FIELDS: 'PROVIDER_SPECIFIC_FIELDS',
  PREVIEW: 'PREVIEW',
  RESULT: 'RESULT'
} as const

type BankTransferStep = (typeof BankTransferSteps)[keyof typeof BankTransferSteps]

type BankTransferFormProps = {
  flowType: WithdrawalSubFlow
}

const BankTransferForm = ({ flowType }: BankTransferFormProps) => {
  const { modalHide, modalShow } = useContext(GlobalContext)
  const { setOtpOnSuccess } = useContext(OtpContext)
  const { setCurrentStep } = useHeaderContext()

  const recipientId = new URLSearchParams(window.location.search).get('recipientId')
  const accountType = useAppSelector(selectMerchantAccountType)

  const [step, setStep] = useState<BankTransferStep>(BankTransferSteps.INITIAL)
  const [result, setResult] = useState(null)
  const [ownAccount, setOwnAccount] = useState<boolean>(true)
  const [appliedPromoCode, setAppliedPromoCode] = useState<string>()

  const [triggered, setTriggered] = useState<boolean>(false)
  const [triggeredRecipientId, setTriggeredRecipientId] = useState<number>(0)

  const history = useHistory()

  const shouldPayApplicationFee = useShouldPayApplicationFeeQry()

  const { uploadedFiles, uploadFileError, resetFiles, getDocumentIds } = useTransactionFilesManagementQry()

  const { returnToOrigin } = useReturnToOrigin()
  const returnToOriginUrl = returnToOrigin({ recipientId })

  const {
    isDataValid,
    formValues,
    handleRecipientChange,
    handlePreviewExtended,
    handleInitiate,
    reloadRecipients,
    selectedAccount,
    setSelectedAccount,
    selectedRecipient,
    filteredRecipients,
    shouldShowWarning,
    accounts,
    accountsError,
    accountsFetching,
    recipientsError,
    recipientsFetching,
    previewFetching,
    previewError,
    prepareData,
    resetMutations,
    isRecurringOpen,
    setIsRecurringOpen,
    recipientDetailsFetching,
    isSepaInstantEnabled
  } = useBankTransferQry({
    flowType,
    editRecipient: (id?: number) => editRecipient(id),
    onPreviewSuccess: () => {
      setStep(BankTransferSteps.PREVIEW)
      setCurrentStep(2)
    },
    onInitiateSuccess: (response: any) => handleInitiateSuccess(response),
    selectAddNew: false,
    recipientId: parseInt(recipientId, 10)
  })

  const {
    fetchProviderSpecificFields,
    providerSpecificFields,
    providerSpecificFieldsError,
    providerSpecificFieldsFetching,
    specificFieldsValues,
    prepareProviderSpecificData
  } = useProviderSpecificFieldsQry()

  const selectedRecipientBankInfo = selectedRecipient?.iban
    ? formatIban(selectedRecipient.iban)
    : selectedRecipient?.accountNumber

  const transferSuccess = (data: any) => {
    setResult(data)
    setStep(BankTransferSteps.RESULT)
    setCurrentStep(3)
  }

  const handleInitiateSuccess = (data: any): void => {
    if (responseDoesNotContainsChallenge(data)) {
      transferSuccess(data)
    }
  }

  const handleRecipientEditSuccess = (data: any) => {
    const { paymentInstrumentId } = data || {}
    reloadRecipients(paymentInstrumentId)
    resetMutations()
    toastSuccess(
      recipientId
        ? tr('FRONTEND.RECIPIENTS.EDIT.SUCCESS', 'Recipient updated.')
        : tr('FRONTEND.RECIPIENTS.CREATE.SUCCESS', 'Recipient successfully created.'),
      undefined,
      {
        duration: 3_000
      }
    )
    modalHide()
  }

  const editRecipient = (recipientId?: number) => {
    const { currencyCode, id } = selectedAccount || {}

    if (currencyCode) {
      modalShow({
        header: recipientId
          ? tr('FRONTEND.RECIPIENTS.EDIT.TITLE', 'Recipient details')
          : tr('FRONTEND.RECIPIENTS.CREATE.TITLE', 'Create recipient'),
        content: (
          <BankRecipientModal
            accountId={id}
            recipientId={recipientId}
            currencyCode={currencyCode}
            onSuccess={handleRecipientEditSuccess}
            initialError={previewError}
            subFlow={flowType}
          />
        )
      })
    } else {
      modalShow({
        header: tr('FRONTEND.RECIPIENTS.CREATE.TITLE', 'Create recipient'),
        content: <NoSuitableAccountModal />,
        options: { size: 'medium', closeOnClickOutside: false }
      })
    }
  }

  const handleContinue = () => {
    const continueToPreview = () => {
      if (isDataValid()) {
        void fetchProviderSpecificFields(prepareData(), (response: any) => {
          if (isValidArray(response)) {
            setStep(BankTransferSteps.PROVIDER_SPECIFIC_FIELDS)
            setCurrentStep(1)
          } else {
            specificFieldsValues.reset()
            internalPreview()
          }
        })
      }
    }

    if (formValues.getValue('reference') !== '') {
      continueToPreview()
    } else {
      modalShow({
        header: tr('FRONTEND.WITHDRAW.BANK_TRANSFER.NO_REFERENCE.TITLE', 'You have not entered a reference'),
        content: <NoReferenceModal onContinue={continueToPreview} />,
        options: { size: 'medium' }
      })
    }
  }

  // A FILE_UPLOAD provider-specific field holds no form value, so it is not covered by
  // specificFieldsValues.areValid() - the uploaded document has to be gated on its own.
  const requiresDocumentUpload = () => providerSpecificFields?.some(field => field.type === 'FILE_UPLOAD') ?? false

  const handleSubmitSpecificFields = () => {
    if (specificFieldsValues.areValid()) {
      internalPreview()
    }
  }

  const internalPreview = () => {
    handlePreviewExtended(getDocumentIds(), prepareProviderSpecificData(), ownAccount)
  }

  const handleAdvancedInitiate = () => {
    setOtpOnSuccess(() => transferSuccess)
    handleInitiate(getDocumentIds(), appliedPromoCode)
  }

  const hasValidPaymentOption = () =>
    selectedRecipient?.paymentOption?.status === PaymentOptionStatuses.AVAILABLE ||
    extractOptionStatusFromAccount(selectedAccount) === PaymentOptionStatuses.AVAILABLE

  useEffect(() => {
    return () => {
      setStep(BankTransferSteps.INITIAL)
      setCurrentStep(1)
      setResult(null)
      resetMutations()
      resetFiles()
    }
  }, [])

  useEffect(() => {
    if (accountType === MerchantAccountTypes.COMPANY) {
      setOwnAccount(hasProperty('ownAccount', selectedRecipient) ? selectedRecipient.ownAccount : true)
    }
  }, [selectedRecipient])

  useEffect(() => {
    if (!recipientsFetching && !!selectedRecipient?.paymentInstrumentId && isValidArray(filteredRecipients)) {
      handleRecipientChange(null, String(selectedRecipient?.paymentInstrumentId ?? ''))
    }
  }, [recipientsFetching])

  useEffect(() => {
    if (
      triggered &&
      !recipientsFetching &&
      triggeredRecipientId === Number(formValues.getValue('destinationPaymentInstrumentId'))
    ) {
      setTriggered(false)
      setTriggeredRecipientId(0)
      handleContinue()
    }
  }, [triggered, recipientsFetching, formValues.getValue('destinationPaymentInstrumentId')])

  return (
    <>
      {step === BankTransferSteps.INITIAL && (
        <div className={styles['send-money-container']} data-test='bank-transfer-container'>
          <div className='from-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.BANK_TRANSFER.FROM', 'From')}</div>
            </div>
            <CorePaymentInput
              selectedOption={selectedAccount}
              setSelectedAccount={setSelectedAccount}
              dataTest={'deposit-payment-input-test'}
              options={accounts}
              enableMaxAmountButton
              paymentOption={selectedRecipient?.paymentOption}
              onChange={(value: any) => formValues.setValue('amount', value)}
              value={formValues.getValue('amount')}
              error={formValues.getError('amount')}
              isLoading={accountsFetching}
              noOptionsMessage={tr(
                'FRONTEND.WITHDRAW.BANK_TRANSFER.NO_ACCOUNTS_WITH_BALANCE',
                'No accounts with enough balance'
              )}
              noOptionsAction={{
                label: tr('FRONTEND.WITHDRAW.NO_ACCOUNT.BUTTON_DEPOSIT', 'Deposit'),
                onClick: () => history.push(paths.banking.deposit())
              }}
            />
          </div>

          <div className='to-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.BANK_TRANSFER.TO', 'To')}</div>

              {!recipientId && accountType !== MerchantAccountTypes.PROSPECT && (
                <MMButton
                  type='button'
                  size='normal'
                  variant='tertiary'
                  leftIcon={RecipientIcon}
                  caption={tr('FRONTEND.RECIPIENTS.LIST.BUTTON_ADD', 'New recipient')}
                  onClick={() => editRecipient()}
                  loading={previewFetching}
                  data-test='button-add-recipient'
                />
              )}
            </div>

            <CoreSelect
              type='default'
              data-test='destination-payment-instrument-id'
              label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.RECIPIENT', 'Recipient')}
              options={filteredRecipients}
              onChange={handleRecipientChange}
              loading={recipientsFetching}
              disabled={recipientsFetching}
              noOptionsMessage={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.NO_RECIPIENTS', 'No recipients created')}
              value={
                selectedRecipient
                  ? {
                      value: selectedRecipient?.paymentInstrumentId,
                      label: selectedRecipient?.name
                    }
                  : null
              }
              LeftIcon={<RecipientIcon />}
            />
            {isValidObject(selectedRecipient) && (
              <div className='recipient-info-container'>
                {isValidString(selectedRecipient.description) && (
                  <div className='recipient-description-wrapper'>
                    <div className='heading'>
                      {tr('FRONTEND.WITHDRAW.BANK_TRANSFER.RECIPIENT_DESCRIPTION', 'Recipient description')}
                    </div>
                    <div className='description'>{selectedRecipient.description}</div>
                  </div>
                )}
                {isValidString(selectedRecipientBankInfo) && (
                  <div className='recipient-description-wrapper'>
                    <div className='heading'>
                      {tr('FRONTEND.WITHDRAW.BANK_TRANSFER.RECIPIENT_BANK_DETAILS', 'Recipient bank details')}
                    </div>
                    <div className='description'>{selectedRecipientBankInfo}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {recipientDetailsFetching ? (
            <Spinner />
          ) : (
            selectedRecipient &&
            accountType === MerchantAccountTypes.COMPANY && (
              <>
                <div className='radio-group-wrapper'>
                  <div className='radio-group-title'>
                    {tr('FRONTEND.WITHDRAW.BANK_TRANSFER.OWN_ACCOUNT', 'Transfer to own account?')}
                  </div>
                  <CoreRadioButton
                    key='isOwnAccount'
                    radioGroupe={'is-own-account'}
                    buttons={[
                      {
                        value: true,
                        label: tr('FRONTEND.WITHDRAW.BANK_TRANSFER.OWN_ACCOUNT_YES', 'Yes'),
                        dataTest: 'own-account-yes'
                      },
                      {
                        value: false,
                        label: tr('FRONTEND.WITHDRAW.BANK_TRANSFER.OWN_ACCOUNT_NO', 'No'),
                        dataTest: 'own-account-no'
                      }
                    ]}
                    type='circle'
                    checked={ownAccount}
                    onClick={(value: any) => setOwnAccount(value)}
                    flexDirection='row'
                    gap={'2.4rem'}
                  />
                </div>
                {!ownAccount && (
                  <div>
                    <FileUploadPane />
                    <UploadedFilesPane />
                    <div className='send-money-disclaimer' data-test='disclaimer'>
                      {tr(
                        'FRONTEND.WITHDRAW.BANK_TRANSFER.DISCLAIMER',
                        'Failure to provide supporting document/s while initiating a third-party withdrawal may cause delays in processing your transaction.'
                      )}
                    </div>
                  </div>
                )}
              </>
            )
          )}

          {isValidObject(selectedRecipient?.paymentOption) && (
            <SendMoneyMethodInfo
              paymentOption={selectedRecipient.paymentOption}
              currencyCode={selectedAccount?.currencyCode}
            />
          )}

          <CoreInput
            type='text'
            name='reference'
            data-test='reference'
            maxLength={50}
            LeftIcon={<ReferenceIcon />}
            label={tr('FRONTEND.WITHDRAW.BANK_TRANSFER.REFERENCE', 'Reference')}
            {...formValues.registerInput('reference')}
            autoComplete='off'
          />

          <AccountingCategorySelect formValues={formValues} />

          <RecurringToggleBox
            onToggle={() => setIsRecurringOpen(!isRecurringOpen)}
            isRecurringOpen={isRecurringOpen}
            recurringDetailsFormValues={formValues}
          />

          {accountType === MerchantAccountTypes.PROSPECT &&
            (shouldPayApplicationFee ? (
              <CoreApplicationFeeAnnounce />
            ) : shouldShowWarning() || !hasValidPaymentOption() ? (
              <CoreOnboardingAnnounce />
            ) : null)}

          <div className='form-actions'>
            <ErrorDisplay
              error={[accountsError, recipientsError, previewError, uploadFileError, providerSpecificFieldsError]}
            />

            <MMButton
              type='button'
              size='large'
              variant='alternative'
              caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
              onClick={handleContinue}
              loading={previewFetching || providerSpecificFieldsFetching}
              disabled={
                accountsFetching ||
                recipientsFetching ||
                selectedRecipient?.paymentOption?.status !== PaymentOptionStatuses.AVAILABLE ||
                formValues.getFloat('amount') <= 0 ||
                (accountType === MerchantAccountTypes.COMPANY && !ownAccount && !isValidArray(uploadedFiles))
              }
              data-test='button-continue'
              fullWidth
            />
          </div>
        </div>
      )}

      {step === BankTransferSteps.PROVIDER_SPECIFIC_FIELDS && (
        <div className={styles['send-money-container']}>
          <div className='from-to-label-container'>
            <div className='from-to-label'>
              {tr(
                'FRONTEND.WITHDRAW.BANK_TRANSFER.PROVIDER_SPECIFIC_FIELDS',
                'Additional information we need in order to make the payment'
              )}
              :
            </div>
          </div>

          <ProviderSpecificFields
            setup={providerSpecificFields}
            formValues={specificFieldsValues}
            beneficiaryName={selectedRecipient?.holderName || selectedRecipient?.name}
          />

          <div className='form-actions'>
            <ErrorDisplay error={[previewError, uploadFileError]} />

            <MMButton
              type='button'
              size='large'
              variant='alternative'
              caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
              loading={previewFetching}
              onClick={handleSubmitSpecificFields}
              disabled={requiresDocumentUpload() && !isValidArray(uploadedFiles)}
              data-test='button-continue'
              fullWidth
            />
          </div>
        </div>
      )}

      {step === BankTransferSteps.PREVIEW && (
        <BankTransferPreview
          formValues={formValues}
          selectedAccount={selectedAccount}
          selectedRecipient={selectedRecipient}
          loading={recipientsFetching}
          onSubmit={handleAdvancedInitiate}
          onCancel={() => {
            setStep(BankTransferSteps.INITIAL)
            setCurrentStep(1)
          }}
          // Pass setState directly - no wrapper needed!
          onApplyPromoCode={setAppliedPromoCode}
          transactionDocumentIds={getDocumentIds()}
          ownAccount={ownAccount}
          subFlow={flowType}
          sepaInstantEnabled={isSepaInstantEnabled}
        />
      )}

      {step === BankTransferSteps.RESULT && (
        <SuccessPane response={result} onClick={() => history.push(returnToOriginUrl)} />
      )}
    </>
  )
}

export default BankTransferForm
